import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';

interface Grant {
  id: string;
  operationId: string;
  operationKey: string;
  serviceKey: string;
  exposure: string;
  pinnedVersion: string | null;
  currentVersion: string | null;
}

interface Toolset {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isActive: boolean;
  clientApp: { key: string; name: string } | null;
  developerTokenCount: number;
  grants: Grant[];
}

export function ToolsetsPage({ role }: { role: string }) {
  const toolsets = useResource<{ items: Toolset[] }>('/api/admin/mcp-toolsets');
  const operations = useResource<{ items: Array<{ id: string; operationKey: string }> }>(
    '/api/admin/operations',
  );
  const apps = useResource<{ items: Array<{ id: string; key: string }> }>('/api/admin/client-apps');
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: '', name: '', description: '', clientAppId: '' });
  const [grantFor, setGrantFor] = useState<Toolset | null>(null);
  const [grantForm, setGrantForm] = useState({
    operationId: '',
    exposure: 'CATALOG_ONLY',
    pinnedVersion: '',
  });
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  return (
    <>
      <PageHeader
        title="Zestawy narzędzi MCP"
        description="Zestaw wyznacza, co agent w danym projekcie w ogóle zobaczy. Operacje spoza zestawu są dla niego niewidoczne i niewywoływalne."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              Nowy zestaw
            </Button>
          ) : undefined
        }
      />

      <Callout>
        Trzymaj domyślny zestaw narzędzi bezpośrednich (DIRECT) mały i dopasowany do projektu.
        Pozostałe operacje udostępniaj jako CATALOG_ONLY — agent dotrze do nich przez{' '}
        <code className="ah-mono">hub_invoke</code> po wyszukaniu w katalogu.
      </Callout>

      {toolsets.loading ? (
        <Skeleton rows={4} />
      ) : (toolsets.data?.items ?? []).length === 0 ? (
        <Card>
          <EmptyState
            title="Brak zestawów"
            description="Zestaw to najprostszy sposób ograniczenia agenta do jednego projektu."
          />
        </Card>
      ) : (
        (toolsets.data?.items ?? []).map((toolset) => (
          <Card
            key={toolset.id}
            title={<span className="ah-mono">{toolset.key}</span>}
            description={`${toolset.name}${toolset.clientApp ? ` · aplikacja ${toolset.clientApp.key}` : ''} · ${toolset.developerTokenCount} tokenów`}
            actions={
              canWrite ? (
                <Button
                  size="sm"
                  onClick={() => {
                    setGrantFor(toolset);
                    setGrantForm({ operationId: '', exposure: 'CATALOG_ONLY', pinnedVersion: '' });
                  }}
                >
                  Dodaj operację
                </Button>
              ) : undefined
            }
            tight
          >
            <DataTable
              rows={toolset.grants}
              rowKey={(row) => row.id}
              empty={
                <EmptyState
                  title="Zestaw jest pusty"
                  description="Agent z tym zestawem zobaczy wyłącznie narzędzia katalogowe."
                />
              }
              columns={[
                {
                  key: 'operation',
                  header: 'Operacja',
                  render: (row) => <span className="ah-mono">{row.operationKey}</span>,
                },
                {
                  key: 'exposure',
                  header: 'Ekspozycja',
                  render: (row) => (
                    <Badge tone={row.exposure === 'DIRECT' ? 'info' : 'neutral'}>
                      {row.exposure}
                    </Badge>
                  ),
                },
                {
                  key: 'version',
                  header: 'Wersja',
                  render: (row) => (
                    <>
                      <span className="ah-mono">
                        {row.pinnedVersion ?? row.currentVersion ?? '—'}
                      </span>
                      {row.pinnedVersion && <Badge tone="warn">przypięta</Badge>}
                    </>
                  ),
                },
                {
                  key: 'actions',
                  header: '',
                  align: 'right',
                  render: (row) =>
                    canWrite ? (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={async () => {
                          try {
                            await api.delete(`/api/admin/mcp-toolset-grants/${row.id}`);
                            toolsets.reload();
                          } catch (error) {
                            const described = describeError(error);
                            toast.error(described.title, described.body);
                          }
                        }}
                      >
                        Usuń
                      </Button>
                    ) : null,
                },
              ]}
            />
          </Card>
        ))
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Nowy zestaw narzędzi"
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.key || !form.name}
              onClick={async () => {
                try {
                  await api.post('/api/admin/mcp-toolsets', {
                    key: form.key,
                    name: form.name,
                    ...(form.description ? { description: form.description } : {}),
                    ...(form.clientAppId ? { clientAppId: form.clientAppId } : {}),
                  });
                  toast.success('Zestaw utworzony', form.key);
                  setOpen(false);
                  setForm({ key: '', name: '', description: '', clientAppId: '' });
                  toolsets.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Utwórz
            </Button>
          </>
        }
      >
        <Field label="Klucz">
          {(id) => (
            <TextInput
              id={id}
              value={form.key}
              onChange={(event) => setForm({ ...form, key: event.target.value })}
              placeholder="projekt-sklep"
            />
          )}
        </Field>
        <Field label="Nazwa">
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>
        <Field
          label="Aplikacja kliencka"
          hint="Opcjonalne powiązanie — ułatwia generowanie pakietu integracyjnego."
        >
          {(id) => (
            <Select
              id={id}
              value={form.clientAppId}
              onChange={(event) => setForm({ ...form, clientAppId: event.target.value })}
            >
              <option value="">— brak —</option>
              {(apps.data?.items ?? []).map((app) => (
                <option key={app.id} value={app.id}>
                  {app.key}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Opis">
          {(id) => (
            <TextInput
              id={id}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={grantFor !== null}
        onOpenChange={(open2) => !open2 && setGrantFor(null)}
        title="Dodaj operację do zestawu"
        description={grantFor ? `Zestaw ${grantFor.key}` : ''}
        footer={
          <>
            <Button onClick={() => setGrantFor(null)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!grantForm.operationId}
              onClick={async () => {
                if (!grantFor) return;
                try {
                  await api.post(`/api/admin/mcp-toolsets/${grantFor.id}/grants`, {
                    operationId: grantForm.operationId,
                    exposure: grantForm.exposure,
                    ...(grantForm.pinnedVersion ? { pinnedVersion: grantForm.pinnedVersion } : {}),
                  });
                  toast.success('Operacja dodana do zestawu');
                  setGrantFor(null);
                  toolsets.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Dodaj
            </Button>
          </>
        }
      >
        <Field label="Operacja">
          {(id) => (
            <Select
              id={id}
              value={grantForm.operationId}
              onChange={(event) => setGrantForm({ ...grantForm, operationId: event.target.value })}
            >
              <option value="">— wybierz —</option>
              {(operations.data?.items ?? []).map((operation) => (
                <option key={operation.id} value={operation.id}>
                  {operation.operationKey}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Ekspozycja"
          hint="DIRECT tworzy typowane narzędzie MCP; CATALOG_ONLY wymaga wcześniejszego wyszukania."
        >
          {(id) => (
            <Select
              id={id}
              value={grantForm.exposure}
              onChange={(event) => setGrantForm({ ...grantForm, exposure: event.target.value })}
            >
              <option value="CATALOG_ONLY">CATALOG_ONLY</option>
              <option value="DIRECT">DIRECT</option>
            </Select>
          )}
        </Field>
        <Field label="Przypięta wersja" hint="Opcjonalne. Zamraża kontrakt dla tego projektu.">
          {(id) => (
            <TextInput
              id={id}
              value={grantForm.pinnedVersion}
              onChange={(event) =>
                setGrantForm({ ...grantForm, pinnedVersion: event.target.value })
              }
              placeholder="1.0.0"
            />
          )}
        </Field>
      </Modal>
    </>
  );
}
